import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiMail, FiLock, FiEye, FiEyeOff, FiUser, FiCheck, FiX, FiLogIn, FiPhone } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import logger from '../../utils/logger';
import { normalizeName } from '../../utils/nameNormalizer';
// Note: SignUp is rendered inside AuthContainer/AuthCard from App.js
// So we don't need AuthPage wrapper here, just render content directly

const SignUpContainer = styled.div`
  width: 100%;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  position: relative;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: var(--control-radius, var(--card-radius));
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background-color: var(--background-primary);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const Icon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--text-secondary);
    }
  }
`;

const Button = styled.button`
  width: 100%;
  height: 48px;
  padding: 0.875rem 1.5rem;
  background-color: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: var(--control-radius, var(--card-radius));
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      filter: brightness(0.95);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

// Soft Alert Banner
const AlertBanner = styled(motion.div)`
  background: ${props => props.type === 'error' 
    ? 'rgba(220, 53, 69, 0.08)' 
    : 'rgba(56, 161, 105, 0.08)'};
  border: 1px solid ${props => props.type === 'error' 
    ? 'rgba(220, 53, 69, 0.25)' 
    : 'rgba(56, 161, 105, 0.25)'};
  border-radius: var(--card-radius);
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const AlertIcon = styled.div`
  color: ${props => props.type === 'error' 
    ? 'rgba(220, 53, 69, 0.9)' 
    : 'rgba(56, 161, 105, 0.9)'};
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

const AlertText = styled.div`
  color: ${props => props.type === 'error' 
    ? 'rgba(220, 53, 69, 0.95)' 
    : 'rgba(56, 161, 105, 0.95)'};
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  flex: 1;
`;

const ErrorMessage = styled(AlertBanner).attrs({ type: 'error' })``;
const SuccessMessage = styled(AlertBanner).attrs({ type: 'success' })``;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: var(--border-color);
  }
  
  span {
    padding: 0 1rem;
    color: var(--text-muted);
    font-size: 0.875rem;
  }
`;

const LoginLink = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: var(--text-secondary);
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 500;
    
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        color: var(--primary-dark);
        text-decoration: underline;
      }
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PasswordRequirements = styled.div`
  background: var(--background-tertiary, var(--background-secondary));
  border: 1px solid var(--border-color);
  border-radius: var(--card-radius);
  padding: 0.75rem;
  margin-top: 0.5rem;
`;

const Requirement = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;

  &:last-child {
    margin-bottom: 0;
  }

  &.valid {
    color: var(--success-color);
  }

  &.invalid {
    color: var(--error-color);
  }

  &.info {
    color: var(--text-muted);
    font-size: 0.8rem;
  }
`;

const RequirementIcon = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.valid ? 'var(--success-color)' : 'var(--error-color)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
  font-size: 8px;
  font-weight: bold;
  
  ${props => props.children === '•' && `
    background: var(--text-muted);
    color: var(--background-primary);
    font-size: 10px;
    font-weight: bold;
  `}
`;

const EmailMatchIndicator = styled.div`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.matches ? 'var(--success-color)' : 'var(--error-color)'};
`;

const EmailExistsMessage = styled.div`
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: rgba(26, 54, 93, 0.1);
  border: 1px solid rgba(26, 54, 93, 0.3);
  border-radius: var(--card-radius);
  font-size: 0.75rem;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const EmailLoginLink = styled(Link)`
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: 0.25rem;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--primary-dark);
      text-decoration: underline;
    }
  }
`;

const Title = styled.h2`
  text-align: center;
  width: 100%;
  margin-bottom: 1.25rem;
  margin-top: 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
`;

const CheckingEmailText = styled.div`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

function SignUp() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailExists, setEmailExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailCheckTimeout, setEmailCheckTimeout] = useState(null);
  
  const { signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-fill email if passed from login flow
  useEffect(() => {
    if (location.state?.email) {
      setFormData(prev => ({
        ...prev,
        email: location.state.email,
        confirmEmail: location.state.email
      }));
    }
  }, [location.state?.email]);

  // Preserve redirect parameter in localStorage when component mounts or URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const redirectFromQuery = urlParams.get('redirect');
    const redirectFromState = location.state?.from?.pathname;
    
    // Store redirect in localStorage so it persists through errors
    const redirectToStore = redirectFromQuery || redirectFromState;
    if (redirectToStore) {
      localStorage.setItem('faithinsight_login_redirect', redirectToStore);
    }
  }, [location.search, location.state]);

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 10,
      digit: /\d/.test(password),
      specialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    };
    return requirements;
  };

  const passwordRequirements = validatePassword(formData.password);

  const checkEmailExists = async (email) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailExists(false);
      return;
    }

    setCheckingEmail(true);
    try {
      // Check if email exists by trying to get user info
      const response = await apiService.checkUserExists(email);
      setEmailExists(response.exists);
    } catch (error) {
      logger.error('Error checking email', { error, email: formData.email });
      setEmailExists(false);
    } finally {
      setCheckingEmail(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Format phone number as user types
    let formattedValue = value;
    if (name === 'phone') {
      formattedValue = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
    
    if (error) setError('');
    if (success) setSuccess('');
    
    // Check email existence when email field changes
    if (name === 'email') {
      // Clear existing timeout
      if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
      }
      
      // Set new timeout for debounced email check
      const timeoutId = setTimeout(() => {
        checkEmailExists(value);
      }, 500);
      
      setEmailCheckTimeout(timeoutId);
    }
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (formData.firstName.trim().length < 2) {
      setError('First name must be at least 2 characters long');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (formData.lastName.trim().length < 2) {
      setError('Last name must be at least 2 characters long');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (emailExists) {
      setError('An account with this email already exists. Please login instead.');
      return false;
    }
    if (!formData.confirmEmail.trim()) {
      setError('Please confirm your email address');
      return false;
    }
    if (formData.email !== formData.confirmEmail) {
      setError('Email addresses do not match');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return false;
    }
    // Validate phone number format (exactly 10 digits)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      setError('Phone number must contain exactly 10 digits');
      return false;
    }
    if (formData.password.length < 10) {
      setError('Password must be at least 10 characters long');
      return false;
    }
    if (!/\d/.test(formData.password)) {
      setError('Password must contain at least one digit');
      return false;
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password)) {
      setError('Password must contain at least one special character');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');

    // Normalize names (convert all caps to proper case, format as "First Middle Last")
    const normalizedFirstName = normalizeName(formData.firstName);
    const normalizedLastName = normalizeName(formData.lastName);

    // Strip formatting from phone number before sending to backend
    const phoneDigits = formData.phone.replace(/\D/g, '');
    const result = await signup(formData.email, formData.password, normalizedFirstName, normalizedLastName, phoneDigits);
    
    if (result.success) {
      setSuccess('Account created successfully! Redirecting...');
      // Navigate immediately without delay
      // Check for redirect in query parameters first
      const urlParams = new URLSearchParams(location.search);
      const redirectFromQuery = urlParams.get('redirect');
      
      // Then check for redirect in location state
      const redirectFromState = location.state?.from?.pathname;
      
      // Then check localStorage (preserved from previous visits)
      const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');
      
      // Prioritize query parameter, then state, then localStorage, then default to dashboard
      const redirectUrl = redirectFromQuery || redirectFromState || redirectFromStorage || '/dashboard';
      
      // If redirecting to catalog, use window.location for immediate navigation
      if (redirectUrl === '/catalog') {
        // Use window.location to ensure immediate navigation without showing dashboard
        window.location.href = '/portal/catalog?redirect=/catalog';
        // Clear from localStorage after navigation
        if (redirectFromStorage) {
          localStorage.removeItem('faithinsight_login_redirect');
        }
      } else {
        // Clear from localStorage after successful signup for non-catalog redirects
        if (redirectFromStorage) {
          localStorage.removeItem('faithinsight_login_redirect');
        }
        navigate(redirectUrl, { replace: true });
      }
    } else {
      setError(result.error || 'Signup failed. Please try again.');
      // Don't clear redirect on error - keep it for retry
    }
    
    setLoading(false);
  };

  return (
    <SignUpContainer>
          <Title>Create Account</Title>
          
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiUser size={16} />
                </Icon>
                <Input
                  type="text"
                  name="firstName"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiUser size={16} />
                </Icon>
                <Input
                  type="text"
                  name="lastName"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiMail size={16} />
                </Icon>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
              {checkingEmail && (
                <CheckingEmailText>
                  Checking email availability...
                </CheckingEmailText>
              )}
              {emailExists && formData.email && !checkingEmail && (() => {
                const redirectFromQuery = new URLSearchParams(location.search).get('redirect');
                const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');
                const redirect = redirectFromQuery || redirectFromStorage;
                const loginUrl = redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login';
                
                return (
                  <EmailExistsMessage>
                    <FiLogIn size={14} />
                    An account with this email already exists.
                    <EmailLoginLink to={loginUrl}>
                      <FiLogIn size={12} />
                      Login here
                    </EmailLoginLink>
                  </EmailExistsMessage>
                );
              })()}
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiMail size={16} />
                </Icon>
                <Input
                  type="email"
                  name="confirmEmail"
                  placeholder="Confirm email address"
                  value={formData.confirmEmail}
                  onChange={handleChange}
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                  required
                />
              </InputWrapper>
              {formData.confirmEmail && formData.email && (
                <EmailMatchIndicator matches={formData.email === formData.confirmEmail}>
                  {formData.email === formData.confirmEmail ? (
                    <>
                      <FiCheck size={14} />
                      Email addresses match
                    </>
                  ) : (
                    <>
                      <FiX size={14} />
                      Email addresses do not match
                    </>
                  )}
                </EmailMatchIndicator>
              )}
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiPhone size={16} />
                </Icon>
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </InputWrapper>
            </FormGroup>

            <PasswordRequirements>
              <div style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
                Password Requirements:
              </div>
              <Requirement className="info">
                <RequirementIcon valid={false}>•</RequirementIcon>
                At least 10 characters
              </Requirement>
              <Requirement className="info">
                <RequirementIcon valid={false}>•</RequirementIcon>
                At least one digit (0-9)
              </Requirement>
              <Requirement className="info">
                <RequirementIcon valid={false}>•</RequirementIcon>
                At least one special character (!@#$%^&*)
              </Requirement>
            </PasswordRequirements>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiLock size={16} />
                </Icon>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </PasswordToggle>
              </InputWrapper>
              {formData.password && (
                <PasswordRequirements>
                  <Requirement className={passwordRequirements.length ? 'valid' : 'invalid'}>
                    <RequirementIcon valid={passwordRequirements.length}>✓</RequirementIcon>
                    At least 10 characters
                  </Requirement>
                  <Requirement className={passwordRequirements.digit ? 'valid' : 'invalid'}>
                    <RequirementIcon valid={passwordRequirements.digit}>✓</RequirementIcon>
                    At least one digit
                  </Requirement>
                  <Requirement className={passwordRequirements.specialChar ? 'valid' : 'invalid'}>
                    <RequirementIcon valid={passwordRequirements.specialChar}>✓</RequirementIcon>
                    At least one special character
                  </Requirement>
                </PasswordRequirements>
              )}
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Icon>
                  <FiLock size={16} />
                </Icon>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Confirm password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </PasswordToggle>
              </InputWrapper>
            </FormGroup>

            {error && (
              <ErrorMessage
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                role="alert"
              >
                <AlertIcon type="error">
                  <FiX />
                </AlertIcon>
                <AlertText type="error">{error}</AlertText>
              </ErrorMessage>
            )}

            {success && (
              <SuccessMessage
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                role="alert"
              >
                <AlertIcon type="success">
                  <FiCheck />
                </AlertIcon>
                <AlertText type="success">{success}</AlertText>
              </SuccessMessage>
            )}

            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <LoadingSpinner />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </Form>

          <Divider>
            <span>or</span>
          </Divider>

          <LoginLink>
            Already have an account?{' '}
            {(() => {
              const redirectFromQuery = new URLSearchParams(location.search).get('redirect');
              const redirectFromStorage = localStorage.getItem('faithinsight_login_redirect');
              const redirect = redirectFromQuery || redirectFromStorage;
              const loginUrl = redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login';
              return <Link to={loginUrl}>Sign in here</Link>;
            })()}
          </LoginLink>
        </SignUpContainer>
  );
}

export default SignUp;
